import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { tipoff, shortDate } from "@/lib/format";
import { PageTitle } from "@/components/AppShell";
import { ProjectionTiles } from "@/components/player/ProjectionTiles";
import { GameChart } from "@/components/player/GameChart";
import { GameLog } from "@/components/player/GameLog";
import { OverUnder } from "@/components/player/OverUnder";

export default function Player() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const gameId = params.get("game");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setData(null);
    api.get(`/players/${id}`, { params: gameId ? { game_id: gameId } : {} })
      .then((r) => setData(r.data)).catch((e) => setError(errorText(e)));
  }, [id, gameId]);

  if (error) return <p data-testid="player-error" className="text-rose-400">{error}</p>;
  if (!data) return <p data-testid="player-loading" className="text-slate-500">Loading player…</p>;
  const { player, game } = data;
  const isHome = game && game.home_team_id === player.team_id;

  return (
    <div className="space-y-6">
      <Link to="/today" data-testid="back-to-today" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-100"><ArrowLeft className="h-4 w-4" /> Today</Link>
      <PageTitle overline={`${player.on_roster ? player.team_name || player.team : "Not on a current ESPN roster"} · ${data.games_played} games stored${player.injury ? ` · ${player.injury}` : ""}`} title={player.name}>
        {game && (
          <div data-testid="player-next-game" className="rounded-md border border-slate-800 bg-slate-900/60 px-4 py-2">
            <div className="text-[11px] uppercase tracking-widest text-slate-500">{game.status === "pre" ? "Next game" : "Selected game"}</div>
            <div className="font-display text-xl font-bold uppercase">{isHome ? `vs ${game.away_code}` : `@ ${game.home_code}`} · {shortDate(game.game_day)} {game.status === "pre" && tipoff(game.date_utc)}</div>
          </div>
        )}
      </PageTitle>
      <ProjectionTiles projection={data.projection} seasonAvg={data.season_avg} seasonLabel={data.season_label} minGames={data.min_games} actual={data.actual} />
      <div className="grid lg:grid-cols-[2fr_1fr] gap-6">
        <GameChart chart={data.chart} />
        <OverUnder playerId={player.id} game={game} projection={data.projection} />
      </div>
      <GameLog rows={data.last10} />
    </div>
  );
}
